import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'

import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import {
  GET_RESOURCE_ACTIVATION_GROUPS_API_ID,
  getResourceActivationGroups,
} from '@/features/resource/endpoints/resources'
import type { ResourceActivationGroupRelation, ResourceActivationGroupsResponse } from '@/features/resource/types'
import type { UseQueryOptions } from '@/types/queries'

type ResourceActivationGroupsQueryParams = {
  resourceUuid: string
}

export type UseResourceActivationGroupsQueryResult = Omit<UseQueryResult, 'data'> & {
  activationGroups: ResourceActivationGroupRelation[]
}

export function useResourceActivationGroupsQuery(
  params: ResourceActivationGroupsQueryParams,
  options?: UseQueryOptions<ResourceActivationGroupsResponse>,
): UseResourceActivationGroupsQueryResult {
  const { data, ...queryResult } = useQuery({
    ...options,
    queryKey: [GET_RESOURCE_ACTIVATION_GROUPS_API_ID, params],
    queryFn: () => getResourceActivationGroups(params.resourceUuid),
  })

  // The failure already reaches Coralogix: the `QueryCache.onError` in `src/index.tsx` logs every
  // failed query, and the hash it logs carries the query key, resource uuid and all.
  useErrorHandler(queryResult.isError)

  return {
    // Note this is the same empty array a resource with no activation groups produces, so callers
    // cannot tell a failed fetch from a genuine zero by looking at it — they must read `isError`,
    // which `queryResult` carries. Rendering "not in any activation group" without checking turns a
    // silent failure into a confident false statement.
    activationGroups: data?.resourceActivationGroupRelations ?? [],
    ...queryResult,
  }
}
